import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Train, Bus, Building2, Plane, Users, ShieldCheck, Lock, BadgePercent, Copy, Clock } from "lucide-react";
import { Header, Footer } from "../components/Layout";
import { SearchWidget } from "../components/SearchWidget";
import { http, ymd } from "../lib/api";
import { PackageCard } from "./Holidays";
import { toast } from "sonner";
import { TravelEnquiry } from "../components/TravelEnquiry";

const TABS = [
  { id: "flights", label: "Flights", icon: Plane },
  { id: "trains", label: "Trains", icon: Train, soon: "Book IRCTC train tickets, check PNR status and live running status." },
  { id: "buses", label: "Buses", icon: Bus, soon: "Over 1 lakh bus routes across India with live tracking." },
  { id: "hotels", label: "Hotels", icon: Building2, soon: "Handpicked stays with verified guest reviews and pay-at-hotel." },
];

const ROUTES = [
  ["DEL", "New Delhi", "BOM", "Mumbai", "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=600&q=70"],
  ["BOM", "Mumbai", "GOI", "Goa", "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=600&q=70"],
  ["BLR", "Bengaluru", "DEL", "New Delhi", "https://images.unsplash.com/photo-1587474260584-136574528ed5?w=600&q=70"],
  ["DEL", "New Delhi", "SXR", "Srinagar", "https://images.unsplash.com/photo-1595815771614-ade9d652a65d?w=600&q=70"],
  ["MAA", "Chennai", "DXB", "Dubai", "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=600&q=70"],
  ["DEL", "New Delhi", "SIN", "Singapore", "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=600&q=70"],
];

const HolidayTeaser = () => {
  const [list, setList] = useState([]);
  useEffect(() => { http.get("/packages").then((r) => setList(r.data)); }, []);
  const pick = [...list.filter((p) => p.region === "international").slice(0, 4), ...list.filter((p) => p.tag === "Near you").slice(0, 2)];
  if (!pick.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 mt-14" data-testid="home-holidays">
      <div className="flex items-end justify-between mb-5">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Holiday packages</h2>
          <p className="text-sm text-slate-500 mt-1">Bali, Dubai, Maldives, Bhutan & all 28 states of India</p>
        </div>
        <Link to="/holidays" data-testid="home-holidays-all" className="text-sm font-semibold text-[#EC5B24]">View all →</Link>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{pick.map((p) => <PackageCard key={p.slug} p={p} />)}</div>
    </section>
  );
};

export default function Home() {
  const [params, setParams] = useSearchParams();
  const tabId = params.get("tab") || "flights";
  const tab = TABS.find((t) => t.id === tabId) || TABS[0];
  const [deals, setDeals] = useState([]);
  useEffect(() => { http.get("/deals").then((r) => setDeals(r.data)); }, []);
  const nextWeek = ymd(new Date(Date.now() + 7 * 864e5));

  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success(`Coupon ${code} copied`); };

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <Header />
      <section className="relative bg-[#1C1A4A] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1499063078284-f78f7d89616a?w=1800&q=70" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1C1A4A]/40 to-[#1C1A4A]" />
        <div className="relative mx-auto max-w-7xl px-4 pt-10 pb-32">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`product-tab-${t.id}`} onClick={() => setParams(t.id === "flights" ? {} : { tab: t.id })}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${tab.id === t.id ? "bg-white text-[#1C1A4A]" : "bg-white/10 text-white hover:bg-white/20"}`}>
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mt-10 tracking-tight animate-rise">
            {tab.soon ? <>{tab.label}, <span className="text-[#EC5B24]">booked for you.</span></> : <>Book flights, <span className="text-[#EC5B24]">pay less.</span></>}
          </h1>
          <p className="text-slate-300 mt-3 text-base md:text-lg max-w-xl animate-rise [animation-delay:120ms]">See live fares across all airlines, pay by UPI and let our team book your ticket.</p>
        </div>
      </section>

      <div className="relative mx-auto max-w-7xl px-4 -mt-24 animate-rise [animation-delay:200ms]">
        {tab.soon ? <TravelEnquiry tab={tab} /> : <SearchWidget />}
      </div>

      <section className="mx-auto max-w-7xl px-4 mt-10 grid gap-4 md:grid-cols-3">
        {[
          [ShieldCheck, "Live fares", "Real-time prices from every airline, updated on each search"],
          [Lock, "Pay by UPI", "Pay directly from Google Pay, PhonePe, Paytm or any UPI app"],
          [BadgePercent, "We book for you", "Our team issues your ticket and shares the PNR in My Trips"],
        ].map(([Icon, t, d]) => (
          <div key={t} className="bg-white rounded-2xl p-5 flex gap-4 border border-slate-100 hover:-translate-y-0.5 transition-transform">
            <span className="h-11 w-11 rounded-xl bg-orange-50 text-[#EC5B24] grid place-items-center shrink-0"><Icon className="h-5 w-5" /></span>
            <div><div className="font-heading font-bold text-slate-900">{t}</div><div className="text-sm text-slate-500">{d}</div></div>
          </div>
        ))}
      </section>

      <section id="offers" className="mx-auto max-w-7xl px-4 mt-14" data-testid="offers-section">
        <div className="flex items-end justify-between mb-5">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Offers for you</h2>
            <p className="text-sm text-slate-500 mt-1">Apply these codes at checkout</p>
          </div>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-3 snap-x">
          {deals.map((d, i) => (
            <div key={d.code} data-testid={`offer-card-${d.code}`}
              className={`snap-start shrink-0 w-72 rounded-2xl p-5 text-white relative overflow-hidden ${["bg-[#1C1A4A]", "bg-[#EC5B24]", "bg-[#0F766E]", "bg-[#7C2D12]", "bg-[#312E81]"][i % 5]}`}>
              <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-white/10" />
              <span className="text-[10px] uppercase tracking-widest font-bold bg-white/20 rounded-full px-2 py-1">{d.tag}</span>
              <div className="font-heading text-xl font-bold mt-3 leading-snug">{d.title}</div>
              <div className="text-sm text-white/75 mt-1">{d.description}</div>
              <button onClick={() => copy(d.code)} data-testid={`copy-coupon-${d.code}`}
                className="mt-4 flex items-center gap-2 border border-dashed border-white/60 rounded-lg px-3 py-1.5 font-mono text-sm font-bold hover:bg-white/10">
                {d.code} <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </section>

      <HolidayTeaser />

      <section className="mx-auto max-w-7xl px-4 mt-14">
        <Link to="/group-booking" data-testid="home-group-banner" className="group flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl bg-[#1C1A4A] text-white p-6 sm:p-8 hover:bg-[#12102E] transition-colors">
          <span className="h-12 w-12 rounded-xl bg-[#EC5B24] grid place-items-center shrink-0"><Users className="h-6 w-6" /></span>
          <div className="flex-1">
            <div className="font-heading text-xl sm:text-2xl font-bold">Travelling with 10 or more?</div>
            <div className="text-slate-300 text-sm mt-1">Special group fares for weddings, school trips, corporate travel and pilgrimages.</div>
          </div>
          <span className="rounded-full bg-white text-[#1C1A4A] font-semibold px-5 py-2.5 text-sm w-fit group-hover:translate-x-1 transition-transform">Get a group quote →</span>
        </Link>
      </section>

      <section className="mx-auto max-w-7xl px-4 mt-14" data-testid="popular-routes">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-5">Popular flight routes</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ROUTES.map(([f, fc, t, tc, img]) => (
            <Link key={f + t} data-testid={`route-${f}-${t}`}
              to={`/flights?from=${f}&to=${t}&fromCity=${fc}&toCity=${tc}&date=${nextWeek}&adults=1&children=0&infants=0&cabin=economy`}
              className="group bg-white rounded-2xl overflow-hidden border border-slate-100 flex hover:shadow-lg transition-shadow">
              <img src={img} alt={tc} className="h-24 w-28 object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="p-4 flex flex-col justify-center">
                <div className="font-heading font-bold text-slate-900">{fc} → {tc}</div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Clock className="h-3 w-3" /> {f} – {t} · Next week</div>
                <div className="text-xs font-semibold text-[#EC5B24] mt-2">View fares →</div>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
